import type { Container, Operation } from '@packages/core/container';

import { Ssm5Client } from '@packages/clients';

import type { Ssm5FreezeFramesDocumentFormat } from '../types';

import { toFreezeFramesDocumentFormat } from '../helpers/mappers';

/** Parameters of {@link GetFreezeFramesDocument}. */
export type GetFreezeFramesDocumentParams = {
    /** The Cloud Connector ID of the open session. */
    cloudConnectorId: string;
    /** The trouble code whose document is requested. */
    dtcCode: string;
    /** The requested document format. */
    format: Ssm5FreezeFramesDocumentFormat;
    /** The module the DTC belongs to. */
    module: string;
    /** The PID name fragment the document is limited to; absent for every PID. */
    search?: string;
    /** Aborts the download. */
    signal?: AbortSignal;
};

/** Download the freeze frame document of a DTC in an open SSM5 session. */
export class GetFreezeFramesDocument implements Operation {
    /**
     * Register this operation with the SSM5 client as its dependency.
     *
     * @param container - the container to register in
     */
    static registerInContainer(container: Container): void {
        container.register(GetFreezeFramesDocument, [Ssm5Client]);
    }

    /** @param client - the SSM5 API client */
    constructor(private client: Ssm5Client) {}

    /**
     * Ask the service for the document in the mapped contract format.
     *
     * @param params - {@link GetFreezeFramesDocumentParams}
     * @returns the file; rejects when aborted or when the download fails
     */
    async exec({
        cloudConnectorId,
        dtcCode,
        format,
        module,
        search,
        signal,
    }: GetFreezeFramesDocumentParams): Promise<Blob> {
        return this.client.getFreezeFramesDocument(
            { cloudConnectorId, dtcCode, format: toFreezeFramesDocumentFormat(format), module, search },
            signal
        );
    }
}
